import { useNavigate } from "react-router-dom";
import { Zap, MapPin, Heart, Trash2, PencilLine, BadgeCheck, MousePointerClick } from "lucide-react";
import { B2Check } from "../b2/kit";
import { track } from "@/lib/analytics";

const BENEFITS = ["100% Free", "No obligation", "You choose who to hire"];
const SUPPORT = [
  { icon: PencilLine, title: "Fast & easy", desc: "Tell us what you need" },
  { icon: BadgeCheck, title: "Free. Always.", desc: "No required payment" },
  { icon: MousePointerClick, title: "You choose", desc: "Compare responses and decide" },
];

export const HomeHero = () => {
  const navigate = useNavigate();
  const goRequest = () => { track("click_post_request_free", { cta: "hero-get-quotes" }); navigate("/app/get-help"); };

  return (
    <section className="relative pt-10 md:pt-16 pb-10 md:pb-16 overflow-hidden bg-white" data-testid="home-hero">
      <div className="pointer-events-none absolute -top-24 right-[30%] w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(22,163,74,0.09),transparent_62%)]" />
      <div className="pointer-events-none absolute top-48 -left-40 w-[420px] h-[420px] rounded-full bg-[radial-gradient(circle,rgba(13,27,61,0.05),transparent_62%)]" />
      <div className="relative max-w-[1300px] mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center lg:min-h-[640px]">
          <div>
            <h1 className="font-display text-[42px] sm:text-[50px] lg:text-[56px] xl:text-[64px] font-extrabold tracking-[-0.03em] leading-[1.05] text-[var(--b2-navy)]" data-testid="hero-headline">
              Need something done?
              <span className="block mt-1.5 lg:hidden">Tell Brivio.</span>
              <span className="block lg:hidden">We&rsquo;ll find <span className="text-[var(--b2-green)]">local pros for you.</span></span>
              <span className="hidden lg:block mt-1.5">Tell Brivio. We&rsquo;ll find</span>
              <span className="hidden lg:block text-[var(--b2-green)]">local pros for you.</span>
            </h1>
            <p className="mt-5 font-display text-[28px] md:text-[32px] font-extrabold text-[var(--b2-navy)] tracking-tight" data-testid="hero-free-always">Free. Always.</p>

            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3" data-testid="hero-benefits">
              {BENEFITS.map((b, i) => (
                <B2Check key={b} testid={`hero-benefit-${i}`} className="text-base">{b}</B2Check>
              ))}
            </div>

            <button data-testid="hero-join" data-cta="hero-post-request" onClick={goRequest}
              className="mt-9 w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-[var(--b2-green)] text-white hover:bg-[var(--b2-green-hover)] rounded-[18px] px-11 py-5 font-display font-extrabold text-lg md:text-xl tracking-wide shadow-[0_16px_40px_rgba(22,163,74,0.35)] transition-all duration-200 hover:-translate-y-0.5">
              <Zap className="w-6 h-6 fill-white" /> Get Free Local Quotes
            </button>

            <p className="mt-6 flex items-start gap-2 text-sm text-[var(--b2-ink-soft)] max-w-md" data-testid="hero-launch-areas">
              <MapPin className="w-4 h-4 text-[var(--b2-green)] shrink-0 mt-0.5" />
              Launching in Plainfield, Bolingbrook, Joliet, Shorewood, Naperville &amp; surrounding areas.
            </p>
          </div>

          {/* dominant image + one product-UI moment */}
          <div className="relative mt-4 lg:mt-0 lg:justify-self-end w-full lg:max-w-[600px]" data-testid="hero-visual">
            <div className="pointer-events-none absolute -top-7 -right-4 w-48 h-48 rounded-[56px] bg-[var(--b2-green-soft)] rotate-[14deg] hidden lg:block" />
            <div className="relative w-full h-[340px] sm:h-[500px] lg:h-[620px] rounded-[28px] lg:rounded-[36px] rounded-tl-[90px] lg:rounded-tl-[130px] overflow-hidden shadow-[0_40px_100px_rgba(10,21,48,0.22)] bg-[#EEF2F7]" data-testid="hero-image-frame">
              <img src="/brand/b2-hero.jpg" alt="A homeowner requesting local help on her phone with Brivio" loading="eager"
                className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 28%" }} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1530]/[0.14] via-transparent to-transparent" />
            </div>
            <div className="absolute top-8 -right-2 sm:right-6 bg-white/95 backdrop-blur rounded-2xl shadow-[0_18px_44px_rgba(10,21,48,0.20)] px-4 py-3 flex items-center gap-2.5" data-testid="hero-float-free">
              <span className="w-9 h-9 rounded-full bg-[var(--b2-green-soft)] flex items-center justify-center shrink-0">
                <Heart className="w-5 h-5 text-[var(--b2-green)] fill-[var(--b2-green)]" />
              </span>
              <span>
                <span className="block font-display font-extrabold text-sm text-[var(--b2-navy)] leading-tight">Free. Always.</span>
                <span className="block text-xs text-[var(--b2-ink-soft)]">No hidden fees</span>
              </span>
            </div>
            {/* product UI moment — clearly example interface */}
            <div className="absolute -bottom-7 -left-2 sm:-left-8 w-[240px] sm:w-[270px] bg-white rounded-[20px] shadow-[0_24px_60px_rgba(10,21,48,0.24)] border border-[var(--b2-border)] p-4" data-testid="hero-request-card">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-[14px] bg-[var(--b2-green-soft)] text-[var(--b2-green)] flex items-center justify-center shrink-0">
                  <Trash2 className="w-6 h-6" />
                </span>
                <span className="min-w-0">
                  <span className="block font-display font-extrabold text-[15px] text-[var(--b2-navy)] leading-tight">Junk Removal</span>
                  <span className="block text-xs text-[var(--b2-ink-soft)] mt-0.5 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> Plainfield, IL</span>
                </span>
              </div>
              <div className="mt-3 pt-3 border-t border-[var(--b2-border)] flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--b2-green)] opacity-60" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[var(--b2-green)]" />
                </span>
                <span className="text-[13px] font-semibold text-[var(--b2-navy)]">Finding local pros&hellip;</span>
              </div>
              <p className="mt-2 text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">Example request</p>
            </div>
          </div>
        </div>

        {/* substantial value cards closing the hero */}
        <div className="mt-16 md:mt-20 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6" data-testid="hero-support-cards">
          {SUPPORT.map((c, i) => (
            <div key={c.title} data-testid={`support-card-${i}`}
              className="bg-white rounded-[22px] border border-[var(--b2-border)] shadow-[0_18px_44px_rgba(10,21,48,0.10)] px-6 py-5 flex items-center gap-4 transition-transform duration-200 hover:-translate-y-1">
              <span className="w-14 h-14 rounded-full bg-[var(--b2-green-soft)] text-[var(--b2-green)] flex items-center justify-center shrink-0 shadow-[inset_0_0_0_1px_rgba(22,163,74,0.15)]">
                <c.icon className="w-7 h-7" />
              </span>
              <span className="min-w-0">
                <span className="block font-display font-extrabold text-base text-[var(--b2-navy)] uppercase tracking-wide leading-tight">{c.title}</span>
                <span className="block text-sm text-[var(--b2-ink-soft)] mt-1">{c.desc}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
